"use client";

import {
  ToggleGroup,
  ToggleGroupItem,
} from "@tripcart/traveler-ui/components/toggle-group";

import { useFormatDate } from "@/lib/datetime-format-context";
import type { Translator } from "@/lib/translations";

import styles from "./departure-filters.module.css";

/**
 * How the list is ordered. Three true sorts; nothing here filters.
 *
 * The guide offers four chips, two of which ("Filling up fast", "Almost sold
 * out") are FILTERS over its scarcity bands. We keep the bands — they are the
 * guide's own thresholds, and both the departure rows and the package cards
 * show them — but not the chips, because a chip that hides every date above a
 * cut-off makes the cut-off decide what a traveller is allowed to see.
 *
 * `seats` is what replaced them: the same intent (show me the ones going
 * first) as an ORDERING over the real number, so nobody loses a departure.
 *
 * This docstring was duplicated, and the surviving copy said the bands "are
 * gone" — true for about a day, then false once they came back.
 *
 * Which of the three a given schedule actually OFFERS is `availableSorts` in
 * `departure-list.logic.ts` — a flat-priced tour gets no "Lowest price" chip,
 * an uncapped one no "Fewest seats left".
 */
export type DepartureSort = "date" | "price" | "seats";

/**
 * The label for a sort option.
 *
 * A switch over literals rather than a `{key, label}` table, because `t()`
 * keys are scraped statically by `pnpm i18n`: `t(option.label)` reads a
 * variable, scrapes to nothing, and the string never reaches the catalog — so
 * every non-English storefront shows it in English forever. `no-restricted-syntax`
 * rejects it outright.
 */
function sortLabel(key: DepartureSort, t: Translator): string {
  switch (key) {
    case "date":
      return t("Soonest");
    case "price":
      return t("Lowest price");
    case "seats":
      return t("Fewest seats left");
  }
}

interface FiltersProps {
  years: number[];
  activeYear: number;
  onYear: (year: number) => void;
  /** Month indices (0–11) that actually have departures in the active year,
   *  in order. A month with nothing to show is not offered — an empty tab is
   *  a dead end the traveller has to discover by clicking it. */
  months: number[];
  activeMonth: number | "all";
  onMonth: (month: number | "all") => void;
  sort: DepartureSort;
  onSort: (sort: DepartureSort) => void;
  /** The sorts this schedule can actually reorder by — `availableSorts` in
   *  `departure-list.logic.ts`. One entry means no row to show at all. */
  sorts: DepartureSort[];
  t: Translator;
}

/**
 * The mobile guide's `.dep-filter`: it STAYS PUT while the departures scroll
 * under it. A schedule can run to forty cards, and a filter that scrolls away
 * means changing month is a scroll back to the top first.
 *
 * `--tc-tour-marker` is the section tab bar's own measured resting edge
 * (published by `_tour-tabs-bar.client.tsx`), so this pins directly under it on
 * both header layouts without a second hardcoded offset. The fallback is the
 * desktop chrome, for the frame before the bar has measured itself.
 *
 * Mobile only: on desktop the filter sits beside a rail that is already sticky,
 * and two things pinned at the same height compete for the eye.
 *
 * The full-bleed tracks `SITE_CONTAINER`'s own gutter, which steps from 16px
 * to 32px at `md`. A flat 16px bleed left the band 16px short of each edge
 * between 768px and 1023px, so the bottom rule that separates the filter from
 * the list stopped before the screen did. The band is `.band` in
 * `departure-filters.module.css`.
 */

export function DepartureFilters({
  years,
  activeYear,
  onYear,
  months,
  activeMonth,
  onMonth,
  sort,
  onSort,
  sorts,
  t,
}: FiltersProps) {
  const formatDate = useFormatDate();
  return (
    <div className={styles.band}>
      {/* Guide `.lbl` — the band needs a name. Without it the year/month row
          reads as chrome belonging to the booking card above it rather than as
          the head of the list below. Phone only: on desktop the filter sits
          directly under the section heading, which already names it. */}
      <span className={styles.bandLabel}>{t("All departures")}</span>
      <div className={styles.row}>
        <YearPicker
          years={years}
          activeYear={activeYear}
          onYear={onYear}
          t={t}
        />

        {/* Month — a horizontally scrollable filter rail: the guide's `.fchip`
            pills on a phone, an underlined tab row from `lg`.
            A toggle group (`aria-pressed`), NOT `tablist`/`tab`: there is no
            tabpanel here — these filter a list that is a sibling, not a panel.
            The kit group gives the rail one tab stop and arrow-key movement.

            Nothing is rendered when the active year has no months at all —
            same rule as `YearPicker` below: a filter rail with only "All" to
            pick is not a choice, and the year is rendering its own empty
            state instead of a list this rail could filter. */}
        {months.length > 0 ? (
          <ToggleGroup
            variant="chip"
            value={String(activeMonth)}
            onValueChange={(month) =>
              onMonth(month === "all" ? "all" : Number(month))
            }
            aria-label={t("Filter by month")}
            className={styles.months}
          >
            <ToggleGroupItem value="all" className={styles.month}>
              {t("All")}
            </ToggleGroupItem>
            {months.map((month) => (
              <ToggleGroupItem
                key={month}
                value={String(month)}
                className={styles.month}
              >
                {/* Agency formatter, not `toLocaleDateString(undefined, …)` —
                    these chips sit directly above rows that use it, so the
                    runtime default would label the chip in one locale and the
                    rows it filters in another. See the note on `DateTile`. */}
                {formatDate(new Date(activeYear, month, 1), {
                  monthStyle: "short",
                  omitDay: true,
                  withYear: false,
                })}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        ) : null}
      </div>

      <SortRow sort={sort} onSort={onSort} sorts={sorts} t={t} />
    </div>
  );
}

/** The sort chips. Split out for the same reason as `YearPicker`: this file's
 *  one exported component was doing three unrelated layout jobs. */
function SortRow({
  sort,
  onSort,
  sorts,
  t,
}: {
  sort: DepartureSort;
  onSort: (sort: DepartureSort) => void;
  sorts: DepartureSort[];
  t: Translator;
}) {
  // One option is not a choice — a lone "Soonest" chip beside a "Sort by"
  // label is a control the traveller cannot use. The whole row goes.
  if (sorts.length <= 1) return null;
  return (
    // Slides on a phone rather than wrapping. Three chips plus the "Sort by"
    // label is 430px of content against 358px of screen, so wrapping cost a
    // whole second row — directly under a filter that is now pinned, where
    // every pixel is taken off the departures themselves.
    // Bleed tracks the container's gutter (16px, then 32px from `md`) — the
    // same as the filter band this row sits inside.
    <div className={styles.sort}>
      <span className={styles.sortLabel}>{t("Sort by")}</span>
      <ToggleGroup
        variant="chip"
        value={sort}
        onValueChange={onSort}
        aria-label={t("Sort by")}
        className={styles.sortChips}
      >
        {sorts.map((key) => (
          <ToggleGroupItem key={key} value={key}>
            {sortLabel(key, t)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
}

/** Year — a segmented pill.
 *
 *  Shown whenever the schedule has a year at all, including a single-year one.
 *  It was hidden below two years on the reasoning that a one-option control is
 *  not a choice; in use that read as a MISSING filter, because the month rail
 *  beside it names months with no year anywhere to anchor them — "Jan" on a
 *  page with no 2027 on it. The year is the label the months need, and it
 *  being a control is secondary.
 *
 *  Nothing is rendered when there are no departures at all: there is no year
 *  to name. The caller never reaches this with an empty schedule anyway —
 *  that case shows its own "no departures scheduled" message a level up —
 *  but the guard documents the real invariant rather than assuming it. */
function YearPicker({
  years,
  activeYear,
  onYear,
  t,
}: {
  years: number[];
  activeYear: number;
  onYear: (year: number) => void;
  t: Translator;
}) {
  if (years.length === 0) return null;
  return (
    <ToggleGroup
      value={String(activeYear)}
      onValueChange={(year) => onYear(Number(year))}
      aria-label={t("Filter by year")}
      className={styles.yearGroup}
    >
      {years.map((year) => (
        <ToggleGroupItem
          key={year}
          value={String(year)}
          className={styles.year}
        >
          {year}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}
